import { Injectable } from '@angular/core';

import { ZipFailure } from '@shared/types';
import {
  isNonRetryableError,
  readRejectionStatus,
  type StatusCarryingError,
} from '@shared/utils/http-status.util';

@Injectable({ providedIn: 'root' })
export class RequestLimiterService {
  private queue: Array<{
    url: string;
    headers?: HeadersInit;
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  }> = [];
  private activeCount = 0;
  private async _next(limit: number, retries: number) {
    if (this.queue.length === 0 || this.activeCount >= limit) {
      return;
    }

    const { url, headers, resolve, reject } = this.queue.shift()!;
    this.activeCount++;

    const reqHeaders = {
      ...headers,
      'X-Force-Strict-Transport-Security': 'true',
      'Strict-Transport-Security':
        'max-age=31536000; includeSubDomains; preload',
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'DENY',
      'X-XSS-Protection': '1; mode=block',
    };

    try {
      const response = await fetch(url, { headers: reqHeaders });

      if (!response.ok) {
        // The status is carried on the error so callers can distinguish a
        // revoked-access failure (401/403/404) from a transient server one.
        const _error: StatusCarryingError = new Error(
          `Failed to fetch ${url}: ${response.statusText || response.status}`,
        );
        _error.status = response.status;
        throw _error;
      }

      // Check content type and handle accordingly
      const contentType = response.headers.get('content-type');
      if (contentType?.toLowerCase().includes('application/json')) {
        const data = await response.json(); // Handle JSON response
        resolve(data);
      } else {
        const blob = await response.blob(); // Handle binary (blob) response like PDF
        resolve({
          body: blob,
          headers: response.headers,
          status: response.status,
        });
      }
    } catch (rejection) {
      /*
       * ERM-13269: an authorization failure is settled, not transient.
       * Retrying a 401/403/404 cannot change the answer — it just multiplies
       * the doomed requests by `retryLimit + 1` per document (40 for a
       * ten-document submission whose access was revoked), burning a
       * permission check and a log line each time, and delaying the toast the
       * user is waiting on.
       */
      if (retries > 0 && !isNonRetryableError(rejection)) {
        console.warn(
          `Retrying request for ${url}... (${retries} retries left)`,
        );
        retries--;
        this.queue.unshift({ url, headers, resolve, reject }); // Re-add request to the queue for retry
      } else {
        console.error(`Request to ${url} failed:`, rejection);
        reject({
          url,
          error: rejection,
          status: readRejectionStatus(rejection),
        });
      }
    } finally {
      this.activeCount--;
      this._next(limit, retries); // Trigger the next request in the queue
    }
  }

  request(
    url: string,
    limit: number,
    retries: number,
    headers?: HeadersInit,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.queue.push({ url, headers, resolve, reject });
      this._next(limit, retries);
    });
  }

  async requestAll(
    urls: string[],
    limit: number,
    retries = 3,
    headers?: HeadersInit,
  ): Promise<{ success: any[]; failed: string[] }> {
    const results: any[] = [];
    const failed: string[] = [];

    const promises = urls.map((url) =>
      this.request(url, limit, retries, headers)
        .then((res) => {
          if (res) results.push(res);
        })
        .catch(() => failed.push(url)),
    );

    await Promise.allSettled(promises);
    return { success: results, failed };
  }

  /**
   * Downloads every document, reporting each settlement as it happens.
   *
   * @param urls - Items carrying a `url` and the originating `file`.
   * @param limit - Maximum concurrent requests.
   * @param retries - Retry attempts per request before it is counted failed.
   * @param headers - Headers applied to every request.
   * @param onSettled - Invoked after each document settles, successfully or
   * not, with the running completed count and the total. This is the only
   * progress signal available for a zipped download — these transfers run
   * through `fetch` without reading the body as a stream, so no byte-based
   * percentage is derivable regardless of what `Content-Length` reports.
   * @returns Successful responses plus the failures, each carrying the HTTP
   * status that produced it so callers can tell revoked access from a server
   * fault.
   *
   * @example
   * ```typescript
   * await requestAllDoc(items, 1, 3, headers, (done, total) =>
   *   postMessage({ type: 'progress', completed: done, total }),
   * );
   * ```
   */
  async requestAllDoc(
    urls: any[],
    limit: number,
    retries = 3,
    headers?: HeadersInit,
    onSettled?: (completed: number, total: number) => void,
  ): Promise<{
    success: any[];
    failed: string[];
    failures: ZipFailure[];
  }> {
    const results: any[] = [];
    const failed: string[] = [];
    const failures: ZipFailure[] = [];
    const total = urls.length;
    let settled = 0;

    /** Reports one settled document to the caller's progress hook. */
    const _reportSettled = (): void => {
      settled += 1;
      onSettled?.(settled, total);
    };

    // Map over URLs to create promises and ensure they're returned
    const promises = urls.map((data: any) => {
      const fileTitle = data.file?.title ?? 'Unknown File';

      return this.request(data.url, limit, retries, headers)
        .then((res) => {
          if (res) {
            results.push({ ...res, title: fileTitle });
          }
          _reportSettled();
        })
        .catch((rejection: { status?: number | null }) => {
          failed.push(fileTitle);
          failures.push({
            title: fileTitle,
            status: rejection?.status ?? null,
          });
          _reportSettled();
        });
    });

    // Wait for all promises to settle
    await Promise.allSettled(promises);

    return { success: results, failed, failures };
  }
}
